import execute from '../hooks/execute';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

/**
 * Template Cache Manager
 * Caches rendered templates in sessionStorage with TTL support
 */
export const templateCache = {
  /**
   * Get cached template HTML
   * @param {string} key - Cache key
   * @returns {string|null} Cached HTML or null if expired/not found
   */
  get(key) {
    try {
      const cached = sessionStorage.getItem(`template_${key}`);
      if (!cached) return null;

      const { html, timestamp, ttl } = JSON.parse(cached);

      // Check if expired (default 5 minutes)
      if (Date.now() - timestamp > ttl) {
        this.remove(key);
        return null;
      }

      return html;
    } catch (error) {
      console.error('Cache get error:', error);
      return null;
    }
  },

  /**
   * Store template HTML in cache
   * @param {string} key - Cache key
   * @param {string} html - HTML content to cache
   * @param {number} ttl - Time to live in milliseconds (default: 5 minutes)
   */
  set(key, html, ttl = 5 * 60 * 1000) {
    try {
      const data = {
        html,
        timestamp: Date.now(),
        ttl,
      };
      sessionStorage.setItem(`template_${key}`, JSON.stringify(data));
    } catch (error) {
      console.error('Cache set error:', error);
      // If quota exceeded, clear old caches
      if (error.name === 'QuotaExceededError') {
        this.clear();
      }
    }
  },

  /**
   * Remove specific cached item
   * @param {string} key - Cache key
   */
  remove(key) {
    try {
      sessionStorage.removeItem(`template_${key}`);
    } catch (error) {
      console.error('Cache remove error:', error);
    }
  },

  /**
   * Clear all template caches
   */
  clear() {
    try {
      const keys = Object.keys(sessionStorage);
      keys.filter((k) => k.startsWith('template_')).forEach((k) => {
        sessionStorage.removeItem(k);
      });
    } catch (error) {
      console.error('Cache clear error:', error);
    }
  },

  /**
   * Get cache statistics
   * @returns {object} Cache statistics
   */
  getStats() {
    try {
      const keys = Object.keys(sessionStorage).filter((k) => k.startsWith('template_'));
      let totalSize = 0;

      keys.forEach((key) => {
        const item = sessionStorage.getItem(key);
        totalSize += item ? item.length : 0;
      });

      return {
        count: keys.length,
        totalSize,
        avgSize: keys.length > 0 ? Math.round(totalSize / keys.length) : 0,
      };
    } catch (error) {
      console.error('Cache stats error:', error);
      return { count: 0, totalSize: 0, avgSize: 0 };
    }
  },
};

/**
 * Template Service
 * API methods for template operations
 */
const templateService = {
  /**
   * Render template with data
   * @param {string} templateId - Template ID
   * @param {object} data - Data for template rendering
   * @param {string} accessToken - Authentication token
   * @returns {Promise<object>} Rendered template response
   */
  async renderTemplate(templateId, data) {
    try {
      // Generate cache key
      const cacheKey = `${templateId}_${JSON.stringify(data)}`;

      // Check cache first
      const cached = templateCache.get(cacheKey);
      if (cached) {
        console.log('Template loaded from cache');
        return {
          success: true,
          data: {
            content: cached,
            fromCache: true,
          },
        };
      }

      // Call API using execute helper
      const result = await execute('POST', `${BASE_URL}/v1/templates/render`, {
        templateId,
        data,
      });

      // Cache successful result
      if (result.success && result.data.content) {
        templateCache.set(cacheKey, result.data.content);
      }

      return result;
    } catch (error) {
      console.error('Template render error:', error);
      throw error;
    }
  },

  /**
   * Get available templates for analysis level
   * @param {string} analysisLevel - Analysis level (page/module/dashboard/executive)
   * @param {string} accessToken - Authentication token
   * @returns {Promise<Array>} Available templates
   */
  async getAvailableTemplates(analysisLevel) {
    try {
      const result = await execute(
        'GET',
        `${BASE_URL}/v1/templates?analysisLevel=${analysisLevel}&isActive=true`
      );
      return result.success ? result.data : [];
    } catch (error) {
      console.error('Get templates error:', error);
      throw error;
    }
  },

  /**
   * Submit feedback for template
   * @param {object} feedbackData - Feedback data
   * @param {string} accessToken - Authentication token
   * @returns {Promise<object>} Submission response
   */
  async submitFeedback(feedbackData) {
    try {
      const result = await execute('POST', `${BASE_URL}/v1/summarize/feedback`, feedbackData);
      return result;
    } catch (error) {
      console.error('Submit feedback error:', error);
      throw error;
    }
  },
};

export default templateService;
